import { List, ListItem, Typography } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { DataGrid } from "@/components/molecules/DataGrid"
import { type FragmentOf, graphql, readFragment, type ResultOf } from "@/graphql"

const graphQLFragments = {
    providerCalendar: graphql(`
        fragment ProviderCalendarTemporalClassesDetails_ProviderCalendar on ProviderCalendar {
            temporalClasses {
                label
                description
            }
        }
    `),
}

type ProviderCalendarTemporalClassItemProps = {
    item: NonNullable<ResultOf<typeof graphQLFragments.providerCalendar>["temporalClasses"]>[number]
}

function ProviderCalendarTemporalClassItem({ item }: ProviderCalendarTemporalClassItemProps) {
    const t = useTranslations("ElectricityAgreementDetailsProviderTemporalClasses")

    if (!item?.description) {
        return null
    }

    return (
        <ListItem>
            {t.rich("item", {
                label: item.label,
                value: item.description,
                labelWrapper: (chunks) => <Typography component="span">{chunks}</Typography>,
            })}
        </ListItem>
    )
}

type ProviderCalendarTemporalClassesDetailsProps = {
    providerCalendar: FragmentOf<typeof graphQLFragments.providerCalendar> | null
}

export function ProviderCalendarTemporalClassesDetails(
    props: ProviderCalendarTemporalClassesDetailsProps
) {
    const t = useTranslations("ElectricityAgreementDetailsProviderTemporalClasses")
    const providerCalendar = readFragment(graphQLFragments.providerCalendar, props.providerCalendar)

    const temporalClassesWithDescription = providerCalendar?.temporalClasses?.filter(
        (temporalClass) => !!temporalClass?.description
    )

    if (!temporalClassesWithDescription?.length) {
        return null
    }

    return (
        <DataGrid.Item gridColumn="span 2">
            <DataGrid.Label>{t("title")}</DataGrid.Label>
            <DataGrid.Value>
                <List>
                    {temporalClassesWithDescription.map((temporalClass) => (
                        <ProviderCalendarTemporalClassItem
                            key={temporalClass?.label}
                            item={temporalClass}
                        />
                    ))}
                </List>
            </DataGrid.Value>
        </DataGrid.Item>
    )
}

ProviderCalendarTemporalClassesDetails.messages = [
    "ElectricityAgreementDetailsProviderTemporalClasses",
] as const

ProviderCalendarTemporalClassesDetails.fragments = graphQLFragments
